import { alpha, Box, keyframes, Typography } from "@mui/material";
import Check from "@mui/icons-material/Check";
import Undo from "@mui/icons-material/Undo";
import moment from "moment";
import { ReactNode } from "react";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { dateFormat, shortDate } from "../../../misc/dates";
import { colorOrNeutral } from "../../../misc/projectColors";
import { useSnackAction } from "../../../misc/SnackAction";
import { useUIFlags } from "../../../misc/UIFlags";
import { useSwipe } from "../../../misc/useSwipe";
import { ProjectCode } from "../../project/ProjectCode";
import { TaskCheckbox } from "../TaskCheckbox";
import { useTaskContextMenu } from "../TaskContextMenu";
import { vibrate } from "../useCompleteTask";

// nudgeMotion slides the row part of the way and back, to show it can be swiped
const nudgeMotion = keyframes`
    0%, 100% { transform: translateX(0); }
    40%, 60% { transform: translateX(56px); }
`;

export function DayTaskRow(props: {
    task: Task;
    disabled?: boolean;
    onClick: () => void;
    onToggle: (checked: boolean) => void;

    // showDue puts the due date in the meta line, for a list that isn't one day
    showDue?: boolean;

    // showTeam puts the team name in the meta line, for the "All" workspace
    showTeam?: boolean;

    // grip is the drag handle at the right-hand end, for a row that can be reordered
    grip?: ReactNode;

    // nudge slides the row a little way on its own, to show a user who has never
    // swiped that the row can be
    nudge?: boolean;
}) {
    const task = props.task;
    const done = !!task.completedAt;

    // a task working towards a project wears that project's colour on its left edge,
    // the same way it does on the calendar. One filed under nothing wears the neutral
    // tint rather than a colour it doesn't have.
    const color = colorOrNeutral(task.projectColor);

    const flags = useUIFlags();

    const archive = useSnackAction(async (input: { id: number }) => {
        await api.tasks.archive(input);
    }, { success: "Archived" });

    const reschedule = useSnackAction(async (input: { id: number; dueDate: string }) => {
        await api.tasks.reschedule(input);
    }, { success: "Moved to today" });

    const menu = useTaskContextMenu<Task>({
        onArchive: task => archive.callback({ id: task.id }),
        onToggle: (_, completed) => props.onToggle(completed),
        onToday: task => reschedule.callback({ id: task.id, dueDate: moment().format(dateFormat) }),
    });

    // a swipe to the right ticks the task off, or re-opens one that's done. A touch
    // and hold opens the same menu a right-click does.
    const swipe = useSwipe({
        disabled: props.disabled,
        onPass: vibrate,
        onSwipe: () => {
            flags.set("swipe", true);
            props.onToggle(!done);
        },
        onHold: point => {
            vibrate();
            menu.openAt(task, point);
        },
    });

    // what the task is due on, when it's due and what it hangs off sit under the
    // title; the line goes altogether when there's none of it
    const carriedDate = task.carriedOver && task.dueDate ? shortDate(task.dueDate) : "";
    const due = props.showDue && task.dueDate && !carriedDate ? shortDate(task.dueDate) : "";
    const time = task.dueTime ? moment(task.dueTime, "HH:mm").format("h:mma") : "";
    const parent = task.type === "checklist" ? task.parentTitle : task.projectName;
    const team = props.showTeam ? task.teamName : "";
    const labels = [parent, team].filter(v => !!v).join(" · ");
    const hasMeta = !!carriedDate || !!due || !!time || !!labels;

    return (
        <Box sx={{ position: "relative", overflow: "hidden", borderBottom: 1, borderColor: "divider" }}>
            {/* what letting go does shows under the row as it slides */}
            {(swipe.offset > 0 || props.nudge) && (
                <Box sx={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", pl: 2,
                    bgcolor: done ? "grey.500" : "success.main",
                    color: "common.white",
                    opacity: swipe.passed ? 1 : 0.6,
                }}>
                    {done ? <Undo /> : <Check />}
                </Box>
            )}

            <Box
                {...swipe.handlers}
                onContextMenu={e => menu.open(task, e)}
                onClick={() => {
                    if (swipe.wasSwipe()) return;
                    props.onClick();
                }}
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    // a thumb needs something to land on, so the row is sized to be tapped
                    // rather than to fit as many tasks on screen as it can
                    minHeight: 56,
                    px: 1,
                    py: 1.25,
                    borderLeft: 4,
                    borderLeftColor: done ? "success.main" : color.border,
                    bgcolor: done ? "grey.50" : "background.paper",
                    cursor: "pointer",
                    // the browser keeps the vertical scroll, and hands the sideways move
                    // to the swipe
                    touchAction: "pan-y",
                    // a touch and hold opens the menu rather than selecting the text
                    userSelect: "none",
                    WebkitTouchCallout: "none",
                    transform: swipe.offset ? `translateX(${swipe.offset}px)` : undefined,
                    transition: swipe.offset ? undefined : "transform 150ms",
                    animation: props.nudge && !swipe.offset ? `${nudgeMotion} 1.2s ease-in-out 0.8s 2` : undefined,
                    "&:active": { bgcolor: "action.selected" },
                }}
            >
                <Box data-no-swipe onClick={e => e.stopPropagation()}
                     sx={{ flexShrink: 0, display: "flex", mt: "-6px", ml: "-6px" }}>
                    <TaskCheckbox
                        checked={done}
                        disabled={props.disabled}
                        // the empty circle wears a washed-out version of the task's own
                        // accent, at the weight grey.400 carries on an untinted row
                        tint={alpha(color.border, 0.45)}
                        onChange={props.onToggle}
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{
                        lineHeight: 1.4,
                        // a long unbroken word breaks rather than widening the page
                        overflowWrap: "anywhere",
                        color: done ? "text.secondary" : "text.primary",
                        textDecoration: done ? "line-through" : undefined,
                    }}>
                        {task.title}
                    </Typography>

                    {hasMeta && (
                        <Typography variant="caption" component="div" noWrap sx={{ color: "text.secondary" }}>
                            {carriedDate && (
                                <Box component="span" sx={{ color: "warning.dark", mr: 0.75 }}>
                                    {carriedDate}
                                </Box>
                            )}
                            {due && <Box component="span" sx={{ mr: 0.75 }}>{due}</Box>}
                            {time && <Box component="span" sx={{ mr: 0.75 }}>{time}</Box>}
                            {labels}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                    <ProjectCode code={task.projectCode} color={task.projectColor} done={done} />
                </Box>

                {props.grip}
            </Box>

            {menu.element}
        </Box>
    );
}
